import React, {useState, useEffect, useRef, useMemo} from 'react';
import classNames from 'classnames';
import {useParams} from 'react-router-dom';
import {TransformWrapper} from 'react-zoom-pan-pinch';
import {useDebouncedCallback} from 'use-debounce';
import {Modal, CircularProgress} from '@material-ui/core';
import {Lock as LockIcon, Close as CloseIcon} from '@material-ui/icons';
import {noop, ternary} from '@pluto/utils';
import {NoteType} from '@pluto/@types';
import QCReports from '@pluto/qc-reports/QCReportsConnected';
import FileImport from '@pluto/file-import-modal';
import EmptyArtwork from 'ContentItemView/ContentItemViewArtwork/emptyArtWork';
import {
    AspRatio,
    ArtworkItem,
    DEBOUNCE_TIME,
    transformWrapperProps,
} from './QCToolsArtworkTypes&Const';
import {
    handleDebounceCallback,
    handleFetchQcArtwork,
    handleModalClose,
    getZoomedImage,
    handleDebounce,
    setActiveImage,
    filterInSmall,
    filterInLarge,
    filterInPoster,
    filterInFeatured,
    filterInSquare,
    handleUpload,
} from './QCToolsArtworkHandlers';
import {QCToolsArtworkThumbnails} from './QCToolsArtworkThumbnails/QCToolsArtworkThumbnails';
import './QCToolsArtworkContainer.scss';

export const selectOpts = [
    {
        placeholder: 'Artwork type',
        enumType: 'artworkTypes',
        sortOrder: [
            'series_featured',
            'series_square',
            'poster',
            'thumbnail_small',
            'thumbnail_large',
        ],
    },
];

export interface QCToolsContainerProps {
    artworkImages: ArtworkItem[];
    isLoading: boolean;
    setQcArtwork: (id: number) => void;
    setDeliverable: (id: number) => void;
    resetQcArtwork: () => void;
    jobDeliveryDate: string;
    setArtworkUpload: () => void;
    setArtworkReplace: () => void;
}

const QCToolsArtworkContainer = ({
    setQcArtwork,
    resetQcArtwork,
    setDeliverable,
    artworkImages: images = [],
    isLoading,
    jobDeliveryDate,
    setArtworkUpload,
    setArtworkReplace,
}: QCToolsContainerProps) => {
    const {id} = useParams<{id: string}>();

    const imagesSmall = useMemo(() => images.filter(filterInSmall), [images]);
    const imagesLarge = useMemo(() => images.filter(filterInLarge), [images]);
    const imagesPoster = useMemo(() => images.filter(filterInPoster), [images]);
    const imagesFeatured = useMemo(() => images.filter(filterInFeatured), [images]);
    const imagesSquare = useMemo(() => images.filter(filterInSquare), [images]);

    const [currentZoomSmall, setCurrentZoomSmall] = useState<number>(NaN);
    const [currentZoomLarge, setCurrentZoomLarge] = useState<number>(NaN);
    const [currentZoomPoster, setCurrentZoomPoster] = useState<number>(NaN);
    const [currentZoomFeatured, setCurrentZoomFeatured] = useState<number>(NaN);
    const [currentZoomSquare, setCurrentZoomSquare] = useState<number>(NaN);

    const [currentSmall, setCurrentSmall] = useState<number>(NaN);
    const [currentLarge, setCurrentLarge] = useState<number>(NaN);
    const [currentPoster, setCurrentPoster] = useState<number>(NaN);
    const [currentFeatured, setCurrentFeatured] = useState<number>(NaN);
    const [currentSquare, setCurrentSquare] = useState<number>(NaN);

    const [isModalOpen, setIsModalOpen] = useState<string>('');

    const thumbnailSmall = useRef({} as HTMLDivElement);
    const thumbnailLarge = useRef({} as HTMLDivElement);
    const thumbnailPoster = useRef({} as HTMLDivElement);
    const thumbnailFeatured = useRef({} as HTMLDivElement);
    const thumbnailSquare = useRef({} as HTMLDivElement);
    const thumbnailRefs = useRef({});

    const fetchArtwork = handleFetchQcArtwork(
        setQcArtwork,
        resetQcArtwork,
        id,
        isLoading
    );

    useEffect(setActiveImage(setCurrentSmall, imagesSmall), [imagesSmall]);
    useEffect(setActiveImage(setCurrentLarge, imagesLarge), [imagesLarge]);
    useEffect(setActiveImage(setCurrentPoster, imagesPoster), [imagesPoster]);
    useEffect(setActiveImage(setCurrentFeatured, imagesFeatured), [imagesFeatured]);
    useEffect(setActiveImage(setCurrentSquare, imagesSquare), [imagesSquare]);

    useEffect(fetchArtwork, []);

    const debounceCallback = handleDebounceCallback(setDeliverable);

    const [debouncedFnSmall] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);
    const [debouncedFnLarge] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);
    const [debouncedFnPoster] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);
    const [debouncedFnFeatured] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);
    const [debouncedFnSquare] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);

    const onDebouncedSetSmall = useMemo(() => handleDebounce(debouncedFnSmall), []);
    const onDebouncedSetLarge = useMemo(() => handleDebounce(debouncedFnLarge), []);
    const onDebouncedSetPoster = useMemo(() => handleDebounce(debouncedFnPoster), []);
    const onDebouncedSetFeatured = useMemo(() => handleDebounce(debouncedFnFeatured), []);
    const onDebouncedSetSquare = useMemo(() => handleDebounce(debouncedFnSquare), []);

    const setTernary = ternary(jobDeliveryDate);

    const lockIcon = setTernary(<LockIcon className="lock-icon" />, null);

    const zoomProps = useMemo(
        () => ({
            [AspRatio.poster]: {
                images: imagesPoster,
                currentZoomId: currentZoomPoster,
                setCurrentZoomId: setCurrentZoomSmall,
                thumbnail: thumbnailPoster,
            },
            [AspRatio.featured]: {
                images: imagesFeatured,
                currentZoomId: currentZoomFeatured,
                setCurrentZoomId: setCurrentZoomFeatured,
                thumbnail: thumbnailFeatured,
            },
            [AspRatio.square]: {
                images: imagesSquare,
                currentZoomId: currentZoomSquare,
                setCurrentZoomId: setCurrentZoomSquare,
                thumbnail: thumbnailSquare,
            },
            [AspRatio.small]: {
                images: imagesSmall,
                currentZoomId: currentZoomSmall,
                setCurrentZoomId: setCurrentZoomSmall,
                thumbnail: thumbnailSmall,
            },
            [AspRatio.large]: {
                images: imagesLarge,
                currentZoomId: currentZoomLarge,
                setCurrentZoomId: setCurrentZoomLarge,
                thumbnail: thumbnailLarge,
            },
        }),
        [
            imagesSmall,
            currentZoomSmall,
            imagesLarge,
            currentZoomLarge,
            imagesPoster,
            currentZoomPoster,
            imagesFeatured,
            currentZoomFeatured,
            imagesSquare,
            currentZoomSquare,
        ]
    );

    const uploadArtwork = handleUpload(id, setArtworkUpload);

    if (isLoading) {
        return <CircularProgress />;
    }

    if (!Boolean(images.length)) {
        return (
            <div className="qc-artwork__empty-artwork">
                <EmptyArtwork>
                    <FileImport
                        btnText="Add Artwork"
                        btnClass="qc-reports__upload"
                        modalTitle="Add Artwork"
                        action={uploadArtwork}
                        className="artwork"
                        acceptedFiles={['image/jpeg', 'image/jpg']}
                        previewComponent={FileImport.ImagePreview}
                        selectOpts={selectOpts}
                    />
                </EmptyArtwork>
            </div>
        );
    }
    const carouselStyles = classNames('carousel', {
        'no-edit': jobDeliveryDate,
    });

    return (
        <div className="qc-artwork">
            <div className="qc-artwork__wrapper">
                <div className="qc-reports__header">Artworks</div>
                <FileImport
                    btnText="Add Artwork"
                    btnClass="qc-reports__upload"
                    modalTitle="Add Artwork"
                    action={uploadArtwork}
                    className="artwork"
                    acceptedFiles={['image/jpeg', 'image/jpg']}
                    previewComponent={FileImport.ImagePreview}
                    selectOpts={selectOpts}
                />
                <div className={carouselStyles}>
                    <QCToolsArtworkThumbnails
                        artworks={imagesFeatured}
                        currentId={currentFeatured}
                        setCurrentId={setTernary(noop, setCurrentFeatured)}
                        currentZoomId={currentZoomFeatured}
                        setCurrentZoomId={setCurrentZoomFeatured}
                        ref={thumbnailFeatured}
                        thumbnailRefs={thumbnailRefs}
                        onDebouncedImageSelect={setTernary(noop, onDebouncedSetFeatured)}
                        handleZoomIn={setIsModalOpen}
                        name={AspRatio.featured}
                        qcId={id}
                        title="Featured"
                        className="carousel__thumbnails_featured"
                        isModalOpen={isModalOpen}
                        icon={lockIcon}
                        setArtworkReplace={setArtworkReplace}
                    />
                    <QCToolsArtworkThumbnails
                        artworks={imagesSquare}
                        currentId={currentSquare}
                        setCurrentId={setTernary(noop, setCurrentSquare)}
                        currentZoomId={currentZoomSquare}
                        setCurrentZoomId={setCurrentZoomSquare}
                        ref={thumbnailSquare}
                        thumbnailRefs={thumbnailRefs}
                        onDebouncedImageSelect={setTernary(noop, onDebouncedSetSquare)}
                        handleZoomIn={setIsModalOpen}
                        name={AspRatio.square}
                        qcId={id}
                        title="Square"
                        className="carousel__thumbnails_square"
                        isModalOpen={isModalOpen}
                        icon={lockIcon}
                        setArtworkReplace={setArtworkReplace}
                    />
                    <QCToolsArtworkThumbnails
                        artworks={imagesPoster}
                        currentId={currentPoster}
                        setCurrentId={setTernary(noop, setCurrentPoster)}
                        currentZoomId={currentZoomPoster}
                        setCurrentZoomId={setCurrentZoomPoster}
                        ref={thumbnailPoster}
                        thumbnailRefs={thumbnailRefs}
                        onDebouncedImageSelect={setTernary(noop, onDebouncedSetPoster)}
                        handleZoomIn={setIsModalOpen}
                        name={AspRatio.poster}
                        qcId={id}
                        title="Poster"
                        className="carousel__thumbnails_poster"
                        isModalOpen={isModalOpen}
                        icon={lockIcon}
                        setArtworkReplace={setArtworkReplace}
                    />
                    <QCToolsArtworkThumbnails
                        artworks={imagesSmall}
                        currentId={currentSmall}
                        setCurrentId={setTernary(noop, setCurrentSmall)}
                        currentZoomId={currentZoomSmall}
                        setCurrentZoomId={setCurrentZoomSmall}
                        ref={thumbnailSmall}
                        thumbnailRefs={thumbnailRefs}
                        onDebouncedImageSelect={setTernary(noop, onDebouncedSetSmall)}
                        handleZoomIn={setIsModalOpen}
                        name={AspRatio.small}
                        qcId={id}
                        title="Screenshot 4x3"
                        className="carousel__thumbnails4x3"
                        isModalOpen={isModalOpen}
                        icon={lockIcon}
                        setArtworkReplace={setArtworkReplace}
                    />
                    <QCToolsArtworkThumbnails
                        artworks={imagesLarge}
                        currentId={currentLarge}
                        setCurrentId={setTernary(noop, setCurrentLarge)}
                        currentZoomId={currentZoomLarge}
                        setCurrentZoomId={setCurrentZoomLarge}
                        ref={thumbnailLarge}
                        thumbnailRefs={thumbnailRefs}
                        onDebouncedImageSelect={setTernary(noop, onDebouncedSetLarge)}
                        handleZoomIn={setIsModalOpen}
                        name={AspRatio.large}
                        qcId={id}
                        title="Screenshot 16x9"
                        className="carousel__thumbnails16x9"
                        isModalOpen={isModalOpen}
                        icon={lockIcon}
                        setArtworkReplace={setArtworkReplace}
                    />
                </div>
                <Modal
                    open={Boolean(isModalOpen)}
                    onClose={handleModalClose(setIsModalOpen)}
                >
                    <div className="qc-artwork-modal">
                        <div
                            role="button"
                            className="qc-artwork-modal__close"
                            onClick={handleModalClose(setIsModalOpen)}
                        >
                            <CloseIcon />
                        </div>
                        <TransformWrapper {...transformWrapperProps}>
                            {getZoomedImage(zoomProps, isModalOpen, thumbnailRefs, noop)}
                        </TransformWrapper>
                    </div>
                </Modal>
            </div>
            <div className="qc-artwork__qc-report">
                <QCReports
                    jobDeliveryDate={jobDeliveryDate}
                    selectReasonPlaceholder="Reason"
                    noteType={NoteType.artwork}
                />
            </div>
        </div>
    );
};

export default QCToolsArtworkContainer;
